import React from 'react';
import { DeliverableTemplate } from '../../types/deliverable';
import {
  FileText,
  Edit,
  Eye,
  CheckSquare,
  Clock,
  TrendingUp,
  Trash2
} from 'lucide-react';
import { Badge } from '../ui/badge';

interface TemplateCardProps {
  template: DeliverableTemplate;
  onView: (template: DeliverableTemplate) => void;
  onEdit: (template: DeliverableTemplate) => void;
  onApply: (template: DeliverableTemplate) => void;
  onDelete: (template: DeliverableTemplate) => void;
  canEdit: boolean;
}

const categoryColors: Record<string, string> = {
  PROPOSAL: 'bg-blue-100 text-blue-800',
  CONTRACT: 'bg-green-100 text-green-800',
  IMPLEMENTATION: 'bg-purple-100 text-purple-800',
  INSTALLATION: 'bg-orange-100 text-orange-800',
  TRAINING: 'bg-pink-100 text-pink-800',
  SUPPORT: 'bg-yellow-100 text-yellow-800',
  DOCUMENTATION: 'bg-indigo-100 text-indigo-800',
  LICENSE: 'bg-teal-100 text-teal-800',
  SERVICE: 'bg-cyan-100 text-cyan-800',
  EQUIPMENT: 'bg-red-100 text-red-800',
  CONSULTATION: 'bg-violet-100 text-violet-800',
  MAINTENANCE: 'bg-amber-100 text-amber-800',
  OTHER: 'bg-gray-100 text-gray-800',
};

export const TemplateCard: React.FC<TemplateCardProps> = ({
  template,
  onView,
  onEdit,
  onApply,
  onDelete,
  canEdit,
}) => {
  return (
    <div className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow p-6 border border-gray-200">
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <FileText className="w-5 h-5 text-gray-600" />
            <h3 className="text-lg font-semibold text-gray-900 line-clamp-1">
              {template.name}
            </h3>
          </div>
          <Badge
            variant="secondary"
            className={categoryColors[template.category] || categoryColors.OTHER}
          >
            {template.category}
          </Badge>
        </div>
        {!template.isActive && (
          <Badge variant="destructive" className="ml-2">
            Inactive
          </Badge>
        )}
      </div>

      {/* Description */}
      {template.description && (
        <p className="text-sm text-gray-600 mb-4 line-clamp-2">
          {template.description}
        </p>
      )}

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4 mb-4">
        <div className="flex items-center gap-2">
          <CheckSquare className="w-4 h-4 text-gray-500" />
          <div>
            <div className="text-xs text-gray-500">Items</div>
            <div className="text-sm font-medium text-gray-900">
              {template.checklistItems?.length || 0}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Clock className="w-4 h-4 text-gray-500" />
          <div>
            <div className="text-xs text-gray-500">Duration</div>
            <div className="text-sm font-medium text-gray-900">
              {template.defaultDuration} days
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <TrendingUp className="w-4 h-4 text-gray-500" />
          <div>
            <div className="text-xs text-gray-500">Used</div>
            <div className="text-sm font-medium text-gray-900">
              {template.usageCount}x
            </div>
          </div>
        </div>
      </div>

      {/* Visibility Badge */}
      <div className="mb-4">
        <Badge
          variant="outline"
          className={template.isPublic ? 'text-green-700' : 'text-blue-700'}
        >
          {template.isPublic ? 'Public' : 'Private'}
        </Badge>
        {template.applyOnStageChange && template.targetStage && (
          <Badge variant="outline" className="ml-2 text-purple-700">
            Auto-apply: {template.targetStage}
          </Badge>
        )}
      </div>

      {/* Actions */}
      <div className="flex gap-2">
        <button
          onClick={() => onView(template)}
          className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-gray-100 text-gray-700 text-sm rounded hover:bg-gray-200 transition-colors"
          aria-label="View template"
        >
          <Eye className="w-4 h-4" />
          View
        </button>
        {canEdit && (
          <button
            onClick={() => onEdit(template)}
            className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 transition-colors"
            aria-label="Edit template"
          >
            <Edit className="w-4 h-4" />
            Edit
          </button>
        )}
        <button
          onClick={() => onApply(template)}
          className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-green-600 text-white text-sm rounded hover:bg-green-700 transition-colors"
          aria-label="Apply template to lead"
        >
          <CheckSquare className="w-4 h-4" />
          Apply
        </button>
        {canEdit && (
          <button
            onClick={() => onDelete(template)}
            className="flex items-center justify-center px-3 py-2 bg-red-100 text-red-700 text-sm rounded hover:bg-red-200 transition-colors"
            aria-label="Delete template"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
};
